/**
 * <petters-slider> — carrousel de héros à rotation automatique.
 *
 * Principes retenus :
 * - la rotation s'arrête au survol, au focus clavier et quand l'onglet passe
 *   en arrière-plan (sinon la barre de progression se désynchronise du visuel) ;
 * - `prefers-reduced-motion` désactive complètement la rotation : on ne
 *   déplace pas le contenu sous les yeux de quelqu'un qui a demandé le contraire ;
 * - toute la navigation reste utilisable au clavier, et la slide active est
 *   annoncée aux lecteurs d'écran.
 */
class PettersSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (this.slides.length === 0) return;

    this.index = 0;
    this.timer = null;
    this.paused = false;
    this.interval = Number(this.dataset.interval || 6000);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, true));
    });

    this.querySelector('[data-prev]')?.addEventListener('click', () => {
      this.goTo(this.index - 1, true);
    });
    this.querySelector('[data-next]')?.addEventListener('click', () => {
      this.goTo(this.index + 1, true);
    });

    // Une pause au survol évite de perdre une slide qu'on est en train de lire.
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.resume();
    });

    this.visibilityHandler = () => {
      if (document.hidden) this.pause();
      else this.resume();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1, true);
      }
    });

    this.reducedHandler = () => (this.reduced.matches ? this.stop() : this.start());
    this.reduced.addEventListener('change', this.reducedHandler);

    this.render();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.reduced?.removeEventListener('change', this.reducedHandler);
  }

  goTo(next, fromUser = false) {
    const total = this.slides.length;
    this.index = ((next % total) + total) % total;
    this.render();
    // Un clic relance le compteur : sinon la slide choisie peut disparaître
    // au bout de quelques dixièmes de seconde.
    if (fromUser) this.start();
  }

  render() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      // Retire du parcours de tabulation les liens des slides masquées.
      slide.querySelectorAll('a, button').forEach((el) => {
        el.tabIndex = active ? 0 : -1;
      });
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }

  start() {
    this.stop();
    if (this.reduced.matches || this.paused || this.slides.length < 2) return;
    this.style.setProperty('--pt-slide-duration', `${this.interval}ms`);
    this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    this.dataset.playing = 'true';
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
    delete this.dataset.playing;
  }

  pause() {
    this.paused = true;
    this.stop();
  }

  resume() {
    this.paused = false;
    this.start();
  }
}

if (!customElements.get('petters-slider')) {
  customElements.define('petters-slider', PettersSlider);
}
